import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { SOCKET_URL } from "../config";

const SocketContext = createContext({
  socket: null,
  isConnected: false,
  subscribe: () => {},
  unsubscribe: () => {},
  reconnect: () => {},
  socketSend: () => {},
});

const useSocket = () => useContext(SocketContext);

const maxReconnectAttempts = 5;

const SocketProvider = ({ children }) => {
  const token = JSON.parse(localStorage.getItem("accessToken"))
  const isLoggedIn = localStorage.getItem("accessToken") ? true : false;

  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const subscribers = useRef([]);
  const retryCount = useRef(0);

  const initializeWebSocket = useCallback(() => {
    const ws = new WebSocket(SOCKET_URL);

    ws.onopen = () => {
      setIsConnected(true);
      retryCount.current = 0;
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event?.data);
        // Call all subscribed callbacks
        subscribers.current.forEach((callback) => callback(data));
      } catch (error) {
        console.error("Failed to parse WebSocket message:");
      }
    };

    ws.onclose = () => {
      console.warn("WebSocket Connection Closed");
      setIsConnected(false);

      if (retryCount.current < maxReconnectAttempts && isLoggedIn) {
        retryCount.current++;
        console.log(`Reconnecting... Attempt ${retryCount.current}`);
        setTimeout(() => reconnect(), 3000);
      } else {
        console.error("Max reconnect attempts reached.");
      }
    };

    ws.onerror = (error) => {
      console.error("WebSocket Error:", error);
    };

    setSocket(ws);

    return () => {
      console.log("Closing WebSocket...");
      ws.close();
    };
  }, [token]);

  const subscribe = useCallback((callback) => {
    subscribers.current.push(callback);
  }, []);

  const unsubscribe = useCallback((callback) => {
    subscribers.current = subscribers.current.filter(
      (subscriber) => subscriber !== callback
    );
  }, []);

  const reconnect = useCallback(() => {
    if (socket) {
      socket.close();
    }
    console.log("Reconnecting WebSocket...");
    initializeWebSocket();
  }, [socket]);

  const socketSend = useCallback(
    (data) => {
      if (socket && socket.readyState === WebSocket.OPEN) {
        try {
          socket.send(JSON.stringify(data));
        } catch (error) {
          console.error("WebSocket Send Error:", error);
        }
      } else {
        console.warn("WebSocket is not connected. Attempting to reconnect...");
        reconnect();
      }
    },
    [socket, isConnected]
  );

  // Establish WebSocket connection on login
  useEffect(() => {
    let cleanupFn;
    if (isLoggedIn) {
      cleanupFn = initializeWebSocket();
    }
    return () => {
      if (cleanupFn) cleanupFn();
    };
  }, [isLoggedIn, initializeWebSocket]);

  // Listen to browser online/offline events
  useEffect(() => {
    const handleOnline = () => {
      console.log("Browser back online");
      if (!isConnected && retryCount.current > maxReconnectAttempts) {
        reconnect();
      }
    };

    window.addEventListener("online", handleOnline);

    return () => {
      window.removeEventListener("online", handleOnline);
    };
  }, [isConnected, reconnect]);

  return (
    <SocketContext.Provider
      value={{
        socket,
        isConnected,
        subscribe,
        unsubscribe,
        reconnect,
        socketSend,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

export { SocketProvider, useSocket };
